/**
 * MessageBubble component
 * Displays a single message with role-based styling and avatar
 * Tutor messages: left-aligned with warm background and otter avatar
 * Student messages: right-aligned with warm background and student avatar
 */

import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import type { Message } from '@/types/session';
import tutorAvatar from '@/assets/tutor-avatar.svg';
import tutorIcon from '@/assets/tutor-icon.png';
import studentAvatar from '@/assets/student-avatar.png';
import { hasMarkdownTable, normaliseMarkdownTable } from '@/lib/tableParser';
import { parseSvgSegments, sanitiseSvg } from '@/lib/svgUtils';

export interface MessageBubbleProps {
  /** Message data to display */
  message: Message;
  /** BUG-20260209-105903: Custom profile image URL for student avatar - 2026-02-09 */
  profileImageUrl?: string | null;
  /**
   * Session type — controls SVG rendering gate.
   * Pass 'math_coach' for Math Coach sessions to enable inline SVG rendering.
   * Omit or leave undefined for all other session types (no SVG processing).
   */
  sessionType?: 'math_coach' | 'english_comprehension';
}

/**
 * MessageBubble - Displays a message with role-based styling and avatar
 * Follows ThinkOtter warm theme with earth tone palette
 */
export function MessageBubble({ message, profileImageUrl, sessionType }: MessageBubbleProps) {
  const isStudent = message.role === 'student';
  const [tutorIconError, setTutorIconError] = useState(false);
  // BUG-20260209-105903: Handle student avatar image load errors with fallback - 2026-02-09
  const [studentIconError, setStudentIconError] = useState(false);

  // Determine which tutor avatar to display
  const tutorAvatarSrc = tutorIconError ? tutorAvatar : tutorIcon;

  // Format timestamp safely with error handling
  const formatTimestamp = (timestamp: string): string => {
    try {
      return format(new Date(timestamp), 'HH:mm');
    } catch {
      return '';
    }
  };

  // Return nothing if content is empty
  if (!message.content || message.content.trim() === '') {
    return null;
  }

  // US-006: SVG rendering gate — only for Math Coach tutor messages
  const isMathCoachTutor = sessionType === 'math_coach' && message.role === 'tutor';

  // BUG-20260709-194338 (T-4 / T-9): synthetic status messages (the "Maximum
  // attempts reached for Question N." / "Question N completed." boundary
  // messages, and — Gate 2 scope expansion — the force-complete closing praise
  // message, both tagged decisionType='question_complete' by the backend) are
  // platform-generated system text, not genuine chat from either party. Render
  // them as a visually distinct, non-selectable/non-copyable system chip so a
  // struggling student cannot select/copy this text back into the answer box
  // (the root cause of this bug). message.content and the existing tip fields
  // (learningTip/showTipPopup) are preserved unchanged for their consumers.
  const isSystemMessage = message.decisionType === 'question_complete';

  if (isSystemMessage) {
    return (
      <div className="mb-4 flex justify-center">
        <span
          className="select-none rounded-full bg-pebbles-100 px-4 py-1.5 text-center text-xs font-medium text-otter-earth-700 shadow-sm"
          style={{ userSelect: 'none', pointerEvents: 'none' }}
        >
          {message.content}
        </span>
      </div>
    );
  }

  return (
    <div className={cn('flex mb-4 items-end gap-2', isStudent ? 'justify-end' : 'justify-start')}>
      {/* Tutor avatar on left */}
      {!isStudent && (
        <img
          src={tutorAvatarSrc}
          alt=""
          aria-hidden="true"
          className="w-11 h-11 rounded-full flex-shrink-0 mb-1 object-cover border-2 border-white shadow-sm"
          onError={() => setTutorIconError(true)}
        />
      )}

      {/* Message bubble — tutor: warm white card; student: warm orange gradient */}
      <div
        className={cn(
          'max-w-[85%] md:max-w-[70%] px-4 py-2.5 rounded-2xl',
          isStudent
            ? 'bg-gradient-to-br from-otter-orange-400 to-otter-orange-500 text-white rounded-br-md shadow-[0_8px_18px_-8px_rgba(234,88,12,0.55)]'
            : 'bg-white border border-otter-sand text-otter-earth-800 rounded-bl-md shadow-sm'
        )}
      >
        {/* BUG-20260224-180125: Render markdown tables for tutor messages containing table syntax */}
        {/* US-006: Three-way conditional: table → SVG (math_coach tutor) → plain text */}
        {!isStudent && hasMarkdownTable(message.content) ? (
          <div className="text-sm md:text-base leading-relaxed markdown-table-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => (
                  <p className="whitespace-pre-wrap mb-2 last:mb-0">{children}</p>
                ),
                table: ({ children }) => (
                  <div className="overflow-x-auto my-2">
                    <table className="w-full border-collapse text-sm">{children}</table>
                  </div>
                ),
                thead: ({ children }) => <thead className="bg-pebbles-100">{children}</thead>,
                th: ({ children }) => (
                  <th className="border border-pebbles-300 px-3 py-2 text-left font-medium text-otter-earth-700">
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td className="border border-pebbles-300 px-3 py-2">{children}</td>
                ),
              }}
            >
              {/* BUG-20260718-070000: repair a missing GFM delimiter row so a table that the
                  generator emitted without one still renders as a table, not raw pipe text. */}
              {normaliseMarkdownTable(message.content)}
            </ReactMarkdown>
          </div>
        ) : isMathCoachTutor ? (
          // US-006: Math Coach tutor — parse content into text + SVG segments
          <div className="space-y-2">
            {parseSvgSegments(message.content).map((segment, index) => {
              if (segment.type === 'svg') {
                const safeSvg = sanitiseSvg(segment.value);
                if (!safeSvg) return null; // malformed SVG: skip silently (AC-006.7)
                return (
                  <div
                    key={index}
                    className="w-full overflow-hidden rounded"
                    dangerouslySetInnerHTML={{ __html: safeSvg }}
                  />
                );
              }
              // Text segment: render with whitespace preservation (unchanged behaviour)
              return segment.value ? (
                <p key={index} className="text-sm md:text-base whitespace-pre-wrap leading-relaxed">
                  {segment.value}
                </p>
              ) : null;
            })}
          </div>
        ) : (
          <p className="text-sm md:text-base whitespace-pre-wrap leading-relaxed">
            {message.content}
          </p>
        )}
        <span
          className={cn('text-xs mt-1 block', isStudent ? 'text-white/80' : 'text-pebbles-500')}
        >
          {formatTimestamp(message.timestamp)}
        </span>
      </div>

      {/* Student avatar on right */}
      {/* BUG-20260209-105903: Use custom profile image if available, fallback to default - 2026-02-09 */}
      {isStudent && (
        <img
          src={profileImageUrl && !studentIconError ? profileImageUrl : studentAvatar}
          alt=""
          aria-hidden="true"
          className="w-8 h-8 rounded-full flex-shrink-0 mb-1 object-cover border-2 border-white shadow-sm"
          onError={() => setStudentIconError(true)}
        />
      )}
    </div>
  );
}
